import React, { useEffect, useState } from 'react';
import { Users, FileText, AlertTriangle } from 'lucide-react';
import { api, DEMO } from '../lib/api';
import PhpGroupSessions from '../components/PhpGroupSessions';
import '../components/PhpTherapy.css';

const NOTE_STATUS_LABELS = { draft: 'Draft', ready_for_signature: 'Ready to sign', signed: 'Signed', voided: 'Voided' };
const NOTE_STATUS_BADGE = { draft: 'badge-warn', ready_for_signature: 'badge-warn', signed: 'badge-ok', voided: 'badge-neutral' };

export default function PhpTherapistDashboard({ user, facility, navigate, notify }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (DEMO) { setData(null); return; }
    api('/php/therapist-dashboard').then(setData).catch(e => notify(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const groups = data?.groups || [];
  const individual = data?.individual || [];
  const requiresAction = data?.requires_action || { unsigned_individual: 0 };
  const actionCount = requiresAction.unsigned_individual;

  const fmtTime = s => s ? new Date(s).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—';

  return <>
    <div className="page-heading"><div><div className="eyebrow">{facility?.name || 'YOUR FACILITY'} · GROUP &amp; INDIVIDUAL THERAPY</div><h1>Therapy sessions.</h1><p>Group psychotherapy with individualized responses, and individual (A/I/R/E) therapy. Signed records are immutable — corrections are addenda.</p></div></div>

    {DEMO && <div className="php-safety-alert" style={{ borderColor: '#b45309', background: '#fffbeb', color: '#92400e' }}><div><strong>Therapy sessions require live data.</strong><p>Sessions, rosters, attendance, and individualized responses are computed server-side and are available in the authenticated (non-demo) workspace.</p></div></div>}

    {!DEMO && data && (
      <section className="content-card">
        <div className="content-heading"><div><h3>My sessions today <span className="badge neutral">{data.date}</span></h3><p>Scoped to your provider identity and facility.</p></div></div>

        {actionCount > 0 && (
          <div className="therapy-requires-action" role="status"><AlertTriangle size={18}/><div><h4>Requires action</h4><ul>
            {requiresAction.unsigned_individual > 0 && <li>{requiresAction.unsigned_individual} unsigned individual note{requiresAction.unsigned_individual === 1 ? '' : 's'}</li>}
          </ul></div></div>
        )}

        <div className="therapy-dashboard-grid">
          <div className="therapy-dashboard-card"><h4><Users size={16}/> Groups</h4>
            {groups.length === 0 && <p className="muted">No groups scheduled.</p>}
            {groups.map(g => (
              <div key={g.id} className="row"><div><strong>{g.name}</strong><small> · {g.program_type?.toUpperCase()} · {fmtTime(g.session_start)}</small><br/><small>{g.assigned_count} participants · {g.signed_count} signed</small></div><button className="text-button" onClick={() => navigate(`/php-therapy/group/${g.id}`)}>Open</button></div>
            ))}
          </div>

          <div className="therapy-dashboard-card"><h4><FileText size={16}/> Individual</h4>
            {individual.length === 0 && <p className="muted">No individual sessions.</p>}
            {individual.map(n => (
              <div key={n.id} className="row"><div><strong>{n.first_name} {n.last_name}</strong> <small>· MRN {n.mrn}</small><br/><small>{n.session_date} · {fmtTime(`${n.session_date}T${n.start_time || ''}`)}</small></div><span className={`badge ${NOTE_STATUS_BADGE[n.status] || 'badge-neutral'}`}>{NOTE_STATUS_LABELS[n.status] || n.status}</span></div>
            ))}
          </div>
        </div>
      </section>
    )}

    <PhpGroupSessions user={user} notify={notify} navigate={navigate} />
  </>;
}
